import React, { useEffect, useState, useRef } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
const testimonials = [{
  quote: 'Armé mi plan de temporada completo en un fin de semana. Ahora sé exactamente qué entrenar cada día y cómo hablarle al grupo antes de cada partido. Nunca más llego a la cancha improvisando.',
  name: 'Sebastián G.',
  location: 'Buenos Aires',
  role: 'Entrenador de futsal barrial'
}, {
  quote: 'Las fichas de evaluación cambiaron todo. Pude hablarles a mis jugadores con datos reales por primera vez. Se comprometieron el doble desde que saben que los sigo de cerca.',
  name: 'Matías R.',
  location: 'Rosario',
  role: 'DT de liga zonal'
}, {
  quote: 'Ahora llego al entrenamiento con la práctica armada en 15 minutos. Antes me llevaba horas buscar ejercicios en YouTube para armar algo que tenga sentido.',
  name: 'Carolina F.',
  location: 'Córdoba',
  role: 'Profesora de Ed. Física'
}, {
  quote: 'Mi equipo pasó de perder por goleada a meterse en semifinales. La diferencia fue tener un sistema táctico claro y saber qué corregir en cada práctica. Recomendado 100%.',
  name: 'Diego L.',
  location: 'Mendoza',
  role: 'DT amateur de barrio'
}, {
  quote: 'Soy ex jugador y siempre me costó transmitir lo que sabía. Con los tomos de mentalidad y gestión del grupo aprendí a pararme como DT. Ahora el plantel me escucha.',
  name: 'Lucas M.',
  location: 'La Plata',
  role: 'Ex jugador y DT formativo'
}];
const TOTAL = testimonials.length;
export function Testimonials() {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const handleScroll = () => {
      const children = container.querySelectorAll<HTMLDivElement>('[data-testimonial-card]');
      if (!children.length) return;
      const containerLeft = container.scrollLeft;
      const containerCenter = containerLeft + container.offsetWidth / 2;
      let closestIdx = 0;
      let closestDist = Infinity;
      children.forEach((child, i) => {
        const childCenter = child.offsetLeft + child.offsetWidth / 2;
        const dist = Math.abs(containerCenter - childCenter);
        if (dist < closestDist) {
          closestDist = dist;
          closestIdx = i;
        }
      });
      setActiveIndex(closestIdx);
    };
    container.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToIndex = (i: number) => {
    const container = scrollRef.current;
    if (!container) return;
    const child = container.querySelectorAll<HTMLDivElement>('[data-testimonial-card]')[i];
    if (!child) return;
    const target = child.offsetLeft - (container.offsetWidth - child.offsetWidth) / 2;
    container.scrollTo({
      left: target,
      behavior: 'smooth'
    });
  };
  const next = () => scrollToIndex(Math.min(activeIndex + 1, TOTAL - 1));
  const prev = () => scrollToIndex(Math.max(activeIndex - 1, 0));
  return <section className="py-8 md:py-14 bg-dark text-white relative overflow-hidden" data-id="element-293">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cancha via-gold to-cancha" data-id="element-294"></div>

      <div className="max-w-6xl mx-auto" data-id="element-295">
        {/* Header */}
        <div className="text-center mb-5 md:mb-8 px-4" data-id="element-296">
          <div className="inline-flex items-center gap-1.5 bg-gold/10 text-gold border border-gold/30 px-3 py-1 rounded-full text-[10px] md:text-xs font-bold uppercase tracking-wider mb-2 md:mb-3" data-id="element-297">
            <Star size={12} fill="currentColor" data-id="element-298" />
            <span data-id="element-299">+500 ENTRENADORES NOS ELIGIERON</span>
          </div>
          <h2 className="font-display text-2xl md:text-4xl text-white uppercase" data-id="element-300">
            Lo que dicen los DTs
          </h2>
          <p className="text-[11px] md:text-sm text-gray-400 font-medium mt-1 md:mt-2" data-id="element-301">
            Deslizá para ver las 5 reseñas
          </p>
        </div>

        {/* Carousel — same for mobile & desktop */}
        <div className="relative" data-id="element-302">
          {/* Desktop nav arrows */}
          <button onClick={prev} disabled={activeIndex === 0} aria-label="Reseña anterior" className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-gray-800 border border-gray-700 hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed items-center justify-center transition-colors" data-id="element-303">
            <ChevronLeft size={20} data-id="element-304" />
          </button>
          <button onClick={next} disabled={activeIndex === TOTAL - 1} aria-label="Reseña siguiente" className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-gray-800 border border-gray-700 hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed items-center justify-center transition-colors" data-id="element-305">
            <ChevronRight size={20} data-id="element-306" />
          </button>

          {/* Scroll track */}
          <div ref={scrollRef} className="flex gap-3 md:gap-5 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2 px-4" data-id="element-307">
            {/* Leading spacer for snap-center math on first card (mobile) */}
            <div className="shrink-0 w-0 md:w-8" aria-hidden="true" data-id="element-308"></div>

            {testimonials.map((t, i) => <TestimonialCard key={i} t={t} index={i} isActive={activeIndex === i} data-id="element-309" />)}

            {/* Trailing spacer so last card can also snap-center */}
            <div className="shrink-0 w-0 md:w-8" aria-hidden="true" data-id="element-310"></div>
          </div>
        </div>

        {/* Dot indicators — visible on all sizes */}
        <div className="flex justify-center items-center gap-1.5 mt-4" data-id="element-311">
          {testimonials.map((_, i) => <button key={i} onClick={() => scrollToIndex(i)} className={`h-1.5 rounded-full transition-all duration-200 ${activeIndex === i ? 'bg-gold w-6' : 'bg-gray-600 hover:bg-gray-500 w-1.5'}`} aria-label={`Ver reseña ${i + 1}`} data-id="element-312" />)}
        </div>

        {/* Counter */}
        <p className="text-center text-[10px] md:text-xs text-gray-500 font-medium mt-2" data-id="element-313">
          {activeIndex + 1} / {TOTAL}
        </p>
      </div>
    </section>;
}
function TestimonialCard({
  t,
  index,
  isActive
}: {
  t: {
    quote: string;
    name: string;
    location: string;
    role: string;
  };
  index: number;
  isActive: boolean;
}) {
  return <motion.div data-testimonial-card initial={{
    opacity: 0,
    y: 15
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.35,
    delay: index * 0.05
  }} className={`bg-gradient-to-br from-gray-800 to-gray-900 p-4 md:p-6 rounded-2xl border relative shrink-0 snap-center flex flex-col w-[85vw] sm:w-[360px] md:w-[340px] transition-all duration-300 ${isActive ? 'border-gold/50 shadow-[0_0_30px_rgba(245,185,33,0.15)]' : 'border-gray-700 opacity-70'}`} data-id="element-314">
      <Quote className="absolute top-3 right-3 text-gray-700 w-6 h-6 md:w-8 md:h-8" data-id="element-315" />

      {/* Stars */}
      <div className="flex gap-0.5 mb-2 md:mb-3 text-gold" data-id="element-316">
        {[...Array(5)].map((_, j) => <Star key={j} size={13} fill="currentColor" data-id="element-317" />)}
      </div>

      {/* Quote */}
      <p className="text-gray-200 text-xs md:text-sm leading-relaxed mb-3 md:mb-4 relative z-10 italic" data-id="element-318">
        "{t.quote}"
      </p>

      {/* Footer */}
      <div className="mt-auto pt-3 border-t border-gray-700/50" data-id="element-319">
        <p className="font-bold text-white text-sm md:text-base leading-tight" data-id="element-320">
          {t.name}
        </p>
        <div className="flex items-center gap-1 text-[10px] md:text-xs mt-1 flex-wrap" data-id="element-321">
          <span className="text-cancha font-semibold" data-id="element-322">📍 {t.location}</span>
          <span className="text-gray-600" data-id="element-323">·</span>
          <span className="text-gray-400" data-id="element-324">{t.role}</span>
        </div>
      </div>
    </motion.div>;
}